/**
 * `GET /v1/invoices/{invoiceId}/payments` for one exception's evidence packet.
 *
 * The existing read hooks in `src/hooks/use-invoices.ts` do not cover this read:
 * `useInvoiceResolutions` returns only the exception's resolutions, and the
 * constituent payments an approver must see before deciding live on the invoice.
 * `listInvoicePayments` is an existing function in the invoicing api module, so
 * this hook adds no transport of its own — it is the same read/never-throw/
 * never-invent contract as the hooks it sits beside, with the same out-of-order
 * guard: a slower response for a previously selected proposal must never land
 * under the packet now on screen.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { ApiErrorInfo } from "@/lib/api";
import { NETWORK_ERROR_CODE, listInvoicePayments } from "@/features/invoicing/api";
import type { Payment } from "@/features/invoicing/types";

export interface InvoicePaymentsResult {
  data: Payment[] | null;
  loading: boolean;
  error: ApiErrorInfo | null;
  refresh: () => void;
}

export function useInvoicePayments(invoiceId: string | undefined): InvoicePaymentsResult {
  const [data, setData] = useState<Payment[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiErrorInfo | null>(null);
  const generation = useRef(0);

  const load = useCallback(async () => {
    const gen = ++generation.current;
    if (!invoiceId) {
      // No invoice to scope the read to: settle, do not issue an unscoped request.
      setData(null);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const page = await listInvoicePayments(invoiceId);
      if (gen !== generation.current) return;
      if (!page.ok) {
        setData(null);
        setError(page.error);
      } else {
        setData(page.items);
      }
    } catch {
      if (gen !== generation.current) return;
      setData(null);
      setError({ reasonCode: NETWORK_ERROR_CODE });
    } finally {
      if (gen === generation.current) setLoading(false);
    }
  }, [invoiceId]);

  useEffect(() => {
    void load();
  }, [load]);

  return { data, loading, error, refresh: load };
}
